import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ServiceBoxSettings {
  image_file: string;
  position_x?: string;
  position_y?: string;
  object_fit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

interface AllServiceBoxSettings {
  forestallningar: ServiceBoxSettings;
  kurser: ServiceBoxSettings;
  underhallning: ServiceBoxSettings;
}

const defaultSettings: ServiceBoxSettings = {
  image_file: '',
  position_x: '50',
  position_y: '50',
  object_fit: 'cover'
};

export const useAllServiceBoxSettings = () => {
  return useQuery({
    queryKey: ['service-box-settings'],
    queryFn: async (): Promise<AllServiceBoxSettings> => {
      // Batch-hämta alla tre settings i ett anrop
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', [
          'service_box_forestallningar',
          'service_box_kurser',
          'service_box_underhallning'
        ]);

      if (error) {
        console.error('Error fetching service box settings:', error);
        throw error;
      }

      // Parse och organisera resultaten
      const settings: AllServiceBoxSettings = {
        forestallningar: { ...defaultSettings },
        kurser: { ...defaultSettings },
        underhallning: { ...defaultSettings }
      };

      if (data) {
        data.forEach((item) => {
          const key = item.key.replace('service_box_', '') as keyof AllServiceBoxSettings;
          try {
            const parsed = JSON.parse(item.value);
            settings[key] = {
              image_file: parsed.image_file || '',
              position_x: parsed.position_x || '50',
              position_y: parsed.position_y || '50',
              object_fit: parsed.object_fit || 'cover'
            };
          } catch (e) {
            console.error(`Error parsing settings for ${key}:`, e);
          }
        });
      }

      return settings;
    },
    staleTime: 5 * 60 * 1000, // Cache i 5 minuter
    gcTime: 10 * 60 * 1000, // Behåll i minnet i 10 minuter
  });
};

export const getImageUrl = (imageFile: string): string => {
  if (!imageFile) return '';
  
  // Om det är en full URL, returnera as-is
  if (/^https?:\/\//i.test(imageFile)) {
    return imageFile;
  }
  
  // Om det startar med /, returnera as-is (lokal path)
  if (imageFile.startsWith('/')) {
    return imageFile;
  }
  
  // Om det ser ut som en Supabase storage fil
  if (imageFile && (imageFile.includes('-') || imageFile.length > 30)) {
    const { data } = supabase.storage.from('images').getPublicUrl(imageFile);
    if (data?.publicUrl) return data.publicUrl;
  }
  
  // Fallback till local public path
  return `/uploads/images/${imageFile}`;
};
